import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { CaseEdits } from './action-types';
import * as api from '../core/api/api';
import HttpMethods from '../enums/http-methods';
import * as cmxDateTime from '../core/date-time/cmx-date-time';
import { fetchActivity, updateProcessInstance } from './code-bench.actions';

type UpdatedValue = {
  fieldKey: string;
  newValue: string;
};

const updateValue = createAction<UpdatedValue>(CaseEdits.UPDATE_VALUE);

type UpdatedCaseFields = {
  caseRunUid: number;
  serviceLine: string;
  caseUpdates: any;
  initialVal: any;
  mappedValues: any;
};

/**
 * Sets a value in the caseUpdates object based on a label-to-value mapping.
 * This is not the ideal way to do this, adding support for CDMX-1832
 * This needs to be handled from field-editor component
 * to deal with label - value combination in the dropdown iteself
 *
 * @param caseUpdateFields - The object containing case update information
 * @param fieldName - The field name to check in caseUpdates
 * @param paramNameToAdd - The parameter name to add the mapped value to in caseUpdates
 */
const setEncounterValueForLabel = (
  caseUpdateFields: UpdatedCaseFields,
  fieldName: string,
  paramNameToAdd: string
) => {
  const { caseUpdates, mappedValues } = caseUpdateFields;
  if (Object.keys(caseUpdates).includes(fieldName)) {
    const encounterVal = mappedValues.find(
      (map: { label: string; value: string }) => {
        return map.label === caseUpdates[fieldName];
      }
    )?.value;

    caseUpdates[paramNameToAdd] = encounterVal;
  }
};

const updateCase = createAsyncThunk<
  void,
  UpdatedCaseFields,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.UPDATE_CASE, async (params, { dispatch }) => {
  const { caseRunUid, serviceLine, caseUpdates, initialVal } = params;

  setEncounterValueForLabel(
    params,
    'scheduling_department',
    'scheduling_department_code'
  );
  setEncounterValueForLabel(params, 'billing_location', 'billing_location');

  try {
    const updatedProcessInstance = await api.requestJSON<
      AmplifyAPI.ProcessInstance
    >(`/coding-api/processinstance/${serviceLine}/${caseRunUid}/v1`, {
      method: HttpMethods.PUT,
      body: JSON.stringify(caseUpdates)
    });

    dispatch(
      updateProcessInstance({ serviceLine, guid: caseRunUid.toString() }) as any
    );
  } catch (e) {
    dispatch(updateValue(initialVal));
  }
});

const patientDetailDictionaries: {
  [key: string]: { dictionaryName: string; columnName: string };
} = {
  billing_location: { dictionaryName: 'Facility', columnName: 'Facility' },
  patient_type: {
    dictionaryName: 'Patient Class',
    columnName: 'Patient Type'
  },
  em_category: { dictionaryName: 'EM Category', columnName: 'E/M Category' },
  scheduling_department: {
    dictionaryName: 'Department',
    columnName: ''
  },
  note_specialty_designation: {
    dictionaryName: 'Note Specialty',
    columnName: 'Note Specialty'
  },
  financial_status_class: {
    dictionaryName: 'Financial Class',
    columnName: 'Financial Status Class'
  }
};

interface loadPatientDetailsPayload {
  activeTenantId: string;
  facility: string;
  dictionary: string;
}

interface loadAllPatientDetailsPayload {
  activeTenantId: string;
  facility: string;
}

interface FieldOption {
  label: string;
  value: string;
}
interface FieldOptions {
  standardFields: FieldOption[];
  additionalFields: FieldOption[];
}

const loadPatientDetailOptions = createAsyncThunk<
  FieldOptions,
  loadPatientDetailsPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.LOAD_PATIENT_DETAIL_OPTIONS, async (payload, _thunkAPI) => {
  const { activeTenantId, facility, dictionary } = payload;
  const dictionaryInfo = patientDetailDictionaries[dictionary];

  let fieldOptions: FieldOptions = {
    standardFields: [],
    additionalFields: []
  };

  try {
    if (dictionaryInfo) {
      let { dictionaryName, columnName } = dictionaryInfo as any;

      const searchParams = new URLSearchParams({
        tenantId: activeTenantId,
        derivedIndicator: 'true',
        effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        dictionaryName: dictionaryName as string,
        columnName: columnName as string
      });

      const res = await api.requestJSON<CMxAPI.RowData>(
        `/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
        {
          method: HttpMethods.GET
        }
      );

      const billing_location_label =
        dictionaryName === 'Facility' &&
        res.rowValues?.some(item => 'Display Name' in item)
          ? 'Display Name'
          : columnName;

      const dictionaryFieldHandlers: {
        [key: string]: (row: { [key: string]: any }) => void;
      } = {
        Department: row => {
          fieldOptions.standardFields.push({
            label: row['Unit/Department'],
            value: row['Unit/Department ID']
          });
        },
        'Patient Class': row => {
          fieldOptions.standardFields.push({
            label: row[columnName],
            value: row[columnName]
          });
          fieldOptions.additionalFields.push({
            label: row['Patient Type'],
            value: row['Patient Class Original']
          });
        },
        Facility: row => {
          fieldOptions.standardFields.push({
            label: row[billing_location_label],
            value: row[columnName]
          });
        },
        'Note Specialty': row => {
          if (row?.['Facility'] === facility) {
            fieldOptions.standardFields.push({
              label: row[columnName],
              value: row[columnName]
            });
          }
        },
        default: row => {
          fieldOptions.standardFields.push({
            label: row[columnName],
            value: row[columnName]
          });
        }
      };

      res.rowValues.forEach((row: { [key: string]: any }) => {
        (
          dictionaryFieldHandlers[dictionaryName] ||
          dictionaryFieldHandlers['default']
        )(row);
      });
    }
  } catch (e) {
    console.log(e);
  }
  return fieldOptions;
});

const loadAllPatientDetailOptions = createAsyncThunk<
  void,
  loadAllPatientDetailsPayload,
  { rejectValue: CMxCommonApp.SubmitError }
>(CaseEdits.LOAD_ALL_PATIENT_DETAIL_OPTIONS, async (payload, { dispatch }) => {
  const { activeTenantId, facility } = payload;
  Object.keys(patientDetailDictionaries).forEach(dictionary => {
    dispatch(
      loadPatientDetailOptions({
        activeTenantId,
        facility,
        dictionary
      })
    );
  });
});

export const caseEditsActions = {
  updateValue,
  updateCase,
  loadAllPatientDetailOptions,
  loadPatientDetailOptions
};
